import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Box,
  Chip,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import HistoryIcon from '@mui/icons-material/History';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// QC endpoints require auth (reads any role, writes manager/admin)
const qcHeaders = () => {
  const token = localStorage.getItem('authToken');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const QualityControl = () => {
  const { currentUser } = useAuth();

  // Screen state: 'start', 'scanning', 'results'
  const [screen, setScreen] = useState('start');

  // Start screen state
  const [availableObds, setAvailableObds] = useState([]);
  const [selectedObd, setSelectedObd] = useState('');
  const [loading, setLoading] = useState(false);

  // Scanning screen state
  const [currentCheck, setCurrentCheck] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [expectedItems, setExpectedItems] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualUpc, setManualUpc] = useState('');
  const [manualQuantity, setManualQuantity] = useState(1);
  const scanInputRef = useRef(null);

  // Results/History state
  const [checkHistory, setCheckHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCheckDetails, setSelectedCheckDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Load available OBDs on mount
  useEffect(() => {
    loadAvailableObds();
    loadCheckHistory();
    loadStats();
  }, []);

  // Auto-focus scan input when on scanning screen
  useEffect(() => {
    if (screen === 'scanning' && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [screen]);

  const loadAvailableObds = async () => {
    try {
      const response = await fetch(`${API_BASE}/quality/available-obds`, { headers: qcHeaders() });
      if (response.ok) {
        const data = await response.json();
        setAvailableObds(data);
      }
    } catch (error) {
      console.error('Error loading available OBDs:', error);
    }
  };

  const loadCheckHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/quality/checks?limit=50`, { headers: qcHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCheckHistory(data.checks);
      }
    } catch (error) {
      console.error('Error loading check history:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/quality/stats?days=30`, { headers: qcHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startQualityCheck = async () => {
    if (!selectedObd) {
      alert('Please select an OBD to check');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/quality/start`, {
        method: 'POST',
        headers: qcHeaders(),
        body: JSON.stringify({
          obd_id: selectedObd,
          checked_by_name: currentUser?.fullName || currentUser?.username || 'Unknown'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentCheck(data.quality_check);
        setExpectedItems(data.expected_items.map(item => ({
          ...item,
          scanned_quantity: 0,
          status: 'pending'
        })));
        setScannedItems([]);
        setScreen('scanning');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to start quality check');
      }
    } catch (error) {
      console.error('Error starting quality check:', error);
      alert('Failed to start quality check');
    }
    setLoading(false);
  };

  const handleScan = useCallback(async (upc, quantity = 1) => {
    if (!currentCheck || !upc.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/quality/${currentCheck.id}/scan`, {
        method: 'POST',
        headers: qcHeaders(),
        body: JSON.stringify({ upc: upc.trim(), quantity })
      });

      if (response.ok) {
        const result = await response.json();
        setLastScanResult(result);

        // Update expected items list
        if (result.is_expected && result.material) {
          setExpectedItems(prev => prev.map(item => {
            if (item.material === result.material) {
              return {
                ...item,
                scanned_quantity: result.scanned_quantity,
                status: result.status
              };
            }
            return item;
          }));
        }

        // Add to scanned items history
        setScannedItems(prev => [{
          upc,
          material: result.material,
          description: result.description,
          quantity,
          status: result.status,
          is_expected: result.is_expected,
          timestamp: new Date()
        }, ...prev]);

        // Clear scan result after 3 seconds
        setTimeout(() => setLastScanResult(null), 3000);
      }
    } catch (error) {
      console.error('Error processing scan:', error);
    }

    setScanInput('');
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [currentCheck]);

  const handleScanKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan(scanInput);
    }
  };

  const handleManualEntry = () => {
    if (manualUpc.trim()) {
      handleScan(manualUpc.trim(), manualQuantity);
      setManualUpc('');
      setManualQuantity(1);
      setManualEntryOpen(false);
    }
  };

  const completeQualityCheck = async () => {
    if (!currentCheck) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/quality/${currentCheck.id}/complete`, {
        method: 'POST',
        headers: qcHeaders(),
        body: JSON.stringify({ notes: '' })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentCheck(data.quality_check);
        setScreen('results');
        loadCheckHistory();
        loadStats();
      }
    } catch (error) {
      console.error('Error completing quality check:', error);
      alert('Failed to complete quality check');
    }
    setLoading(false);
  };

  const loadCheckDetails = async (checkId) => {
    try {
      const response = await fetch(`${API_BASE}/quality/${checkId}/details`, { headers: qcHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSelectedCheckDetails(data);
        setDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error loading check details:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'success';
      case 'partial': return 'warning';
      case 'over': return 'error';
      case 'extra': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <CheckCircleIcon color="success" />;
      case 'partial': return <WarningIcon color="warning" />;
      case 'over': return <ErrorIcon color="error" />;
      case 'extra': return <ErrorIcon color="error" />;
      default: return null;
    }
  };

  const calculateProgress = () => {
    if (expectedItems.length === 0) return 0;
    const checked = expectedItems.filter(item => item.scanned_quantity > 0).length;
    return (checked / expectedItems.length) * 100;
  };

  // Start Screen
  if (screen === 'start') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              Quality Check
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Verify completed OBD contents with UPC barcode scanning
            </Typography>
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Total Checks (30 days)
                </Typography>
                <Typography variant="h3" sx={{ color: '#1976d2' }}>
                  {stats?.summary?.total_checks || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Average Accuracy
                </Typography>
                <Typography variant="h3" sx={{ color: '#26a69a' }}>
                  {stats?.summary?.avg_accuracy ? `${parseFloat(stats.summary.avg_accuracy).toFixed(1)}%` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Available OBDs
                </Typography>
                <Typography variant="h3" sx={{ color: '#ff9800' }}>
                  {availableObds.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Start New Check */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCodeScannerIcon /> Start New Quality Check
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Completed OBD</InputLabel>
                <Select
                  value={selectedObd}
                  onChange={(e) => setSelectedObd(e.target.value)}
                  label="Select Completed OBD"
                >
                  {availableObds.map(obd => (
                    <MenuItem key={obd.id} value={obd.id}>
                      {obd.obd_number} - {obd.ship_to_name} ({obd.line_count} lines, {obd.unit_count} units)
                      {obd.check_count > 0 && ` - ${obd.check_count} previous checks`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={startQualityCheck}
                disabled={loading || !selectedObd}
                sx={{ mt: 3, py: 2 }}
              >
                {loading ? 'Starting...' : 'Start Quality Check'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={loadAvailableObds}
                startIcon={<RefreshIcon />}
                sx={{ mt: 2 }}
              >
                Refresh Available OBDs
              </Button>
            </Paper>
          </Grid>

          {/* Recent Checks */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon /> Recent Quality Checks
              </Typography>

              <TableContainer sx={{ maxHeight: 350 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>OBD</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Checked By</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Accuracy</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {checkHistory.slice(0, 10).map((check) => (
                      <TableRow
                        key={check.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => loadCheckDetails(check.id)}
                      >
                        <TableCell>{check.obd_number}</TableCell>
                        <TableCell>{check.checked_by_name}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={check.accuracy_percentage ? `${check.accuracy_percentage}%` : 'In Progress'}
                            color={
                              !check.accuracy_percentage ? 'default' :
                              parseFloat(check.accuracy_percentage) >= 95 ? 'success' :
                              parseFloat(check.accuracy_percentage) >= 80 ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(check.started_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {checkHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">
                            No quality checks completed yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Check Details Dialog */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Quality Check Details - {selectedCheckDetails?.quality_check?.obd_number}
          </DialogTitle>
          <DialogContent>
            {selectedCheckDetails && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Checked By</Typography>
                    <Typography>{selectedCheckDetails.quality_check.checked_by_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Accuracy</Typography>
                    <Typography variant="h6">
                      {selectedCheckDetails.quality_check.accuracy_percentage}%
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">Correct</Typography>
                    <Typography color="success.main">{selectedCheckDetails.quality_check.correct_count}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">Missing</Typography>
                    <Typography color="error.main">{selectedCheckDetails.quality_check.missing_count}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">Mismatch</Typography>
                    <Typography color="warning.main">{selectedCheckDetails.quality_check.quantity_mismatch_count}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">Extra</Typography>
                    <Typography color="error.main">{selectedCheckDetails.quality_check.extra_count}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>Scan Details</Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Material</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Expected</TableCell>
                        <TableCell align="right">Scanned</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCheckDetails.scans.map((scan) => (
                        <TableRow key={scan.id}>
                          <TableCell>{scan.material}</TableCell>
                          <TableCell>{scan.description}</TableCell>
                          <TableCell align="right">{scan.expected_quantity}</TableCell>
                          <TableCell align="right">{scan.scanned_quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={scan.status}
                              color={getStatusColor(scan.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Scanning Screen
  if (screen === 'scanning') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#1976d2', color: 'white' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5">
                    Checking: {currentCheck?.obd_number}
                  </Typography>
                  <Typography variant="body2">
                    {currentCheck?.total_expected_lines} lines | {currentCheck?.total_expected_units} units expected
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4">
                    {calculateProgress().toFixed(0)}%
                  </Typography>
                  <Typography variant="body2">Progress</Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={calculateProgress()}
                sx={{ mt: 2, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.3)' }}
              />
            </Paper>
          </Grid>

          {/* Scan Input */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCodeScannerIcon /> Scan UPC Barcode
              </Typography>

              <TextField
                fullWidth
                inputRef={scanInputRef}
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyPress={handleScanKeyPress}
                placeholder="Scan or enter UPC barcode..."
                autoFocus
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.5rem',
                    py: 2
                  }
                }}
              />

              {/* Last Scan Result */}
              {lastScanResult && (
                <Alert
                  severity={lastScanResult.is_expected ? 'success' : 'warning'}
                  icon={getStatusIcon(lastScanResult.status)}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="subtitle1">
                    {lastScanResult.material || 'Unknown'} - {lastScanResult.description || 'Unknown Item'}
                  </Typography>
                  <Typography variant="body2">
                    {lastScanResult.is_expected
                      ? `Scanned ${lastScanResult.scanned_quantity} of ${lastScanResult.expected_quantity} expected`
                      : 'Item not expected in this OBD'
                    }
                  </Typography>
                </Alert>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setManualEntryOpen(true)}
                >
                  Manual Entry
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Scans */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Recent Scans</Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                {scannedItems.slice(0, 10).map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getStatusIcon(item.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.material || item.upc}
                      secondary={`Qty: ${item.quantity}`}
                    />
                  </ListItem>
                ))}
                {scannedItems.length === 0 && (
                  <ListItem>
                    <ListItemText
                      secondary="No items scanned yet"
                      sx={{ textAlign: 'center' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Expected Items */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Expected Items</Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Expected</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Scanned</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expectedItems.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: item.status === 'correct' ? 'rgba(76, 175, 80, 0.1)' :
                                  item.status === 'partial' ? 'rgba(255, 152, 0, 0.1)' :
                                  item.status === 'over' ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                        }}
                      >
                        <TableCell>{item.material}</TableCell>
                        <TableCell>{item.material_description}</TableCell>
                        <TableCell align="right">{item.total_quantity}</TableCell>
                        <TableCell align="right">{item.scanned_quantity || 0}</TableCell>
                        <TableCell align="center">
                          {item.status !== 'pending' && (
                            <Chip
                              label={item.status}
                              color={getStatusColor(item.status)}
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={completeQualityCheck}
                disabled={loading}
                sx={{ flex: 1, py: 2 }}
              >
                {loading ? 'Completing...' : 'Complete Quality Check'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => {
                  if (window.confirm('Cancel this quality check? All progress will be lost.')) {
                    setScreen('start');
                    setCurrentCheck(null);
                    setExpectedItems([]);
                    setScannedItems([]);
                  }
                }}
                sx={{ py: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Manual Entry Dialog */}
        <Dialog open={manualEntryOpen} onClose={() => setManualEntryOpen(false)}>
          <DialogTitle>Manual UPC Entry</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="UPC Code"
              value={manualUpc}
              onChange={(e) => setManualUpc(e.target.value)}
              sx={{ mt: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={manualQuantity}
              onChange={(e) => setManualQuantity(parseInt(e.target.value) || 1)}
              sx={{ mt: 2 }}
              inputProps={{ min: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualEntryOpen(false)}>Cancel</Button>
            <Button onClick={handleManualEntry} variant="contained">Add</Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Results Screen
  if (screen === 'results') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" gutterBottom>
                Quality Check Complete
              </Typography>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                OBD: {currentCheck?.obd_number}
              </Typography>

              {/* Accuracy Display */}
              <Box sx={{ my: 4 }}>
                <Typography variant="h1" sx={{
                  color: parseFloat(currentCheck?.accuracy_percentage) >= 95 ? '#4caf50' :
                         parseFloat(currentCheck?.accuracy_percentage) >= 80 ? '#ff9800' : '#f44336',
                  fontWeight: 700
                }}>
                  {currentCheck?.accuracy_percentage}%
                </Typography>
                <Typography variant="h6" color="textSecondary">
                  Accuracy
                </Typography>
              </Box>

              {/* Summary Stats */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={3}>
                  <Card sx={{ bgcolor: 'success.light' }}>
                    <CardContent>
                      <Typography variant="h3" color="success.dark">
                        {currentCheck?.correct_count}
                      </Typography>
                      <Typography color="success.dark">Correct</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card sx={{ bgcolor: 'error.light' }}>
                    <CardContent>
                      <Typography variant="h3" color="error.dark">
                        {currentCheck?.missing_count}
                      </Typography>
                      <Typography color="error.dark">Missing</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card sx={{ bgcolor: 'warning.light' }}>
                    <CardContent>
                      <Typography variant="h3" color="warning.dark">
                        {currentCheck?.quantity_mismatch_count}
                      </Typography>
                      <Typography color="warning.dark">Mismatch</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card sx={{ bgcolor: 'error.light' }}>
                    <CardContent>
                      <Typography variant="h3" color="error.dark">
                        {currentCheck?.extra_count}
                      </Typography>
                      <Typography color="error.dark">Extra</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Lines Checked:</strong> {currentCheck?.lines_checked} of {currentCheck?.total_expected_lines}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Units Checked:</strong> {currentCheck?.units_checked} of {currentCheck?.total_expected_units}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Checked By:</strong> {currentCheck?.checked_by_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Completed:</strong> {new Date(currentCheck?.completed_at).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setScreen('start');
                  setCurrentCheck(null);
                  setExpectedItems([]);
                  setScannedItems([]);
                  setSelectedObd('');
                }}
                sx={{ mt: 4, py: 2, px: 6 }}
              >
                Start New Quality Check
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return null;
};

export default QualityControl;
